import { useTheme } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { OTPInputProps } from 'src/components/common/form/OTPInput/OTPInput.types';

const DEFAULT_OTP_LENGTH = 6;

const OTPInput: React.FC<OTPInputProps> = ({
  numInputs = DEFAULT_OTP_LENGTH,
  value,
  onChange,
  inputStyle,
  errorStyle,
  containerStyle,
  separator,
  onBlur: handleBlur,
  isError = false,
  shouldAutoFocus = false,
}) => {
  const theme = useTheme();
  const [activeInput, setActiveInput] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (shouldAutoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [shouldAutoFocus]);

  useEffect(() => {
    if (inputsRef.current[activeInput]) {
      inputsRef.current[activeInput]?.focus();
    }
  }, [activeInput]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newValue = e.target.value;
    if (/^[0-9]$/.test(newValue) || newValue === '') {
      const newOtpValue = value.split('');
      newOtpValue[index] = newValue;
      onChange(newOtpValue.join(''));
      if (newValue !== '') {
        focusNextInput(index);
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    const input = e.currentTarget;
    const cursorPosition = input.selectionStart;
    const inputLength = input.value.length;

    switch (e.key) {
      case 'Backspace':
        if (!value[index]) {
          focusPrevInput(index, true);
        }
        break;
      case 'ArrowLeft':
        if (cursorPosition === 0) {
          focusPrevInput(index, true);
        }
        break;
      case 'ArrowRight':
        if (cursorPosition === inputLength) {
          focusNextInput(index);
        }
        break;
      default:
        break;
    }
  };

  const handleFocus = (index: number) => () => {
    setActiveInput(index);
  };

  const focusNextInput = (index: number) => {
    if (index < numInputs - 1) {
      const nextInput = index + 1;
      setActiveInput(nextInput);
      setTimeout(() => {
        inputsRef.current[nextInput]?.setSelectionRange(0, 0);
        inputsRef.current[nextInput]?.focus();
      }, 0);
    }
  };

  const focusPrevInput = (index: number, setCursorToEnd = false) => {
    if (index > 0) {
      const prevInput = index - 1;
      setActiveInput(prevInput);
      setTimeout(() => {
        if (setCursorToEnd) {
          const inputLength = inputsRef.current[prevInput]?.value.length || 0;
          inputsRef.current[prevInput]?.setSelectionRange(
            inputLength,
            inputLength
          );
        }
        inputsRef.current[prevInput]?.focus();
      }, 0);
    }
  };

  return (
    <div
      onBlur={handleBlur}
      style={{ display: 'flex', justifyContent: 'center', ...containerStyle }}
    >
      {Array.from({ length: numInputs }).map((_, index) => (
        <React.Fragment key={index}>
          <input
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ''}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onFocus={() => handleFocus(index)}
            autoFocus={shouldAutoFocus && index === 0}
            style={{
              textAlign: 'center',
              borderRadius: '4px',
              border: `1px solid ${
                isError ? theme.palette.error.main : '#E0E0E0'
              }`,
              margin: '5px',
              height: '54px',
              width: '46px',
              color: theme.palette.text.primary,
              fontFamily: 'BentonSans',
              fontSize: '30px',
              fontWeight: 500,
              lineHeight: '42px',
              background: 'transparent',
              ...inputStyle,
              ...(isError ? errorStyle : {}),
            }}
          />
          {index < numInputs - 1 && separator}
        </React.Fragment>
      ))}
    </div>
  );
};

export default OTPInput;
